// Owner and manager sign-in: the phone number, then the 6-digit code received on WhatsApp or, on request, by e-mail
// at the backup address confirmed in the dashboard’s account section (issue #23). The default email tab uses the configured account credentials. Errors come from the API
// with a code; the text shown is in the page language. Numbers and addresses stay LTR inside Arabic.
// The two forms are pure components (props in, markup out) so the tests render them without a DOM.
import { useEffect, useState, type ReactNode } from 'react';
import { UI, retryMinutes, type Lang, type LoginChannel, type UiCopy } from '@ringring/core';
import { api, errorText, type ApiError, type Me } from './api';
import { CredentialsLogin } from './CredentialsLogin';
import { Filled } from './lang';
import { AuthLayout } from './AuthLayout';
import { Icon } from './Icon';

/** The second step: which channel carried the code and what the page may show of the recipient (phone, or masked address). */
export interface CodeStep {
  kind: 'code';
  phone: string;
  channel: LoginChannel;
  display: string;
  sentAt: number;
  resendAfter: number;
}
type Step = { kind: 'phone' } | CodeStep;

/** A refusal kept as data (counts, deadlines, the API code) and worded at render time, in the language on screen. */
export type Problem =
  { code: 'wrong_code'; attemptsLeft: number } | { code: 'locked'; until: number } | { code: 'cooldown'; until: number } | { code: 'error'; error: string | undefined };

/** Text under the field, digits as LTR runs; null once a countdown has run out. */
export function problemText(p: Problem, lang: Lang, now: number): ReactNode {
  const t = UI[lang];
  switch (p.code) {
    case 'wrong_code':
      return <Filled template={t.pin_wrong} vars={{ attempts_left: String(p.attemptsLeft) }} />;
    case 'locked':
      return now < p.until ? <Filled template={t.pin_locked} vars={{ minutes: String(retryMinutes(p.until - now)) }} /> : null;
    case 'cooldown':
      return now < p.until ? <Filled template={t.code_cooldown} vars={{ seconds: String(Math.ceil((p.until - now) / 1000)) }} /> : null;
    case 'error':
      return errorText(lang, p.error);
  }
}

/** The API error of a one-time code request or check, as a Problem; the words come from the copy of its code. */
export function problemOf(err: ApiError): Problem {
  const after = (err.details.retry_after ?? 60) * 1000;
  if (err.code === 'code_locked') return { code: 'locked', until: Date.now() + after };
  if (err.code === 'code_cooldown') return { code: 'cooldown', until: Date.now() + after };
  if (err.code === 'wrong_code') return { code: 'wrong_code', attemptsLeft: err.details.attempts_left ?? 0 };
  return { code: 'error', error: err.code };
}

function Alert({ id, message }: { id: string; message: ReactNode }) {
  return message ? (
    <p id={id} role="alert">
      {message}
    </p>
  ) : null;
}

export interface PhoneFormProps {
  t: UiCopy;
  phone: string;
  setPhone: (v: string) => void;
  busy: boolean;
  locked: boolean;
  message: ReactNode;
  /** Request a code on WhatsApp (the submit) or by e-mail (the secondary button). */
  request: (channel: LoginChannel) => void;
}

export function PhoneForm({ t, phone, setPhone, busy, locked, message, request }: PhoneFormProps) {
  return (
    <form
      className="card signin"
      onSubmit={(e) => {
        e.preventDefault();
        request('whatsapp');
      }}
    >
      <label htmlFor="phone">{t.phone_prompt}</label>
      <input
        id="phone"
        type="tel"
        inputMode="tel"
        autoComplete="tel"
        dir="ltr"
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        disabled={locked || busy}
        aria-describedby={message ? 'signin-message' : 'phone-hint'}
        required
      />
      <p id="phone-hint" className="muted">
        {t.phone_hint}
      </p>
      <Alert id="signin-message" message={message} />
      <button className="btn" type="submit" disabled={locked || busy}>
        <Icon name="message" />
        {t.send_code}
        <Icon name="arrow" className="directional" />
      </button>
      <button className="btn secondary" type="button" onClick={() => request('email')} disabled={locked || busy}>
        {t.send_by_email}
      </button>
    </form>
  );
}

export interface CodeFormProps {
  t: UiCopy;
  step: CodeStep;
  code: string;
  setCode: (v: string) => void;
  busy: boolean;
  locked: boolean;
  message: ReactNode;
  /** Seconds before the same channel may carry another code; 0 when "resend" is available. */
  resendWait: number;
  verify: () => void;
  /** Another code on the same channel, or the first code on the other one ("send by e-mail instead"). */
  request: (channel: LoginChannel) => void;
  changePhone: () => void;
}

export function CodeForm({ t, step, code, setCode, busy, locked, message, resendWait, verify, request, changePhone }: CodeFormProps) {
  const byEmail = step.channel === 'email';
  const other: LoginChannel = byEmail ? 'whatsapp' : 'email';
  return (
    <form
      className="card signin"
      onSubmit={(e) => {
        e.preventDefault();
        verify();
      }}
    >
      <p>{byEmail ? <Filled template={t.code_sent_email} vars={{ email: step.display }} /> : <Filled template={t.code_sent} vars={{ phone: step.display }} />}</p>
      {byEmail ? <p className="muted">{t.email_hint}</p> : null}
      <label htmlFor="code">{byEmail ? t.code_prompt_email : t.code_prompt}</label>
      <input
        id="code"
        className="code"
        inputMode="numeric"
        autoComplete="one-time-code"
        dir="ltr"
        maxLength={7}
        value={code}
        onChange={(e) => setCode(e.target.value)}
        disabled={locked || busy}
        aria-describedby={message ? 'signin-message' : undefined}
        autoFocus
      />
      <Alert id="signin-message" message={message} />
      <button className="btn" type="submit" disabled={locked || busy || code.replace(/\s/g, '').length < 6}>
        {t.sign_in}
      </button>
      <div className="row">
        <button className="btn secondary" type="button" onClick={() => request(step.channel)} disabled={locked || busy || resendWait > 0}>
          {resendWait > 0 ? <Filled template={t.resend_in} vars={{ seconds: String(resendWait) }} /> : t.resend_code}
        </button>
        <button className="btn secondary" type="button" onClick={() => request(other)} disabled={locked || busy}>
          {byEmail ? t.send_by_whatsapp : t.send_by_email}
        </button>
        <button className="btn secondary" type="button" onClick={changePhone}>
          {t.change_phone}
        </button>
      </div>
    </form>
  );
}

interface CodeResponse {
  phone: string;
  phone_display: string;
  channel: LoginChannel;
  email_display: string | null;
  resend_after: number;
}

export function SignIn({ lang, setLang, onSignedIn }: { lang: Lang; setLang: (l: Lang) => void; onSignedIn: (me: Me) => void }) {
  const t = UI[lang];
  const [step, setStep] = useState<Step>({ kind: 'phone' });
  const [method, setMethod] = useState<'email' | 'phone'>('email');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<Problem | null>(null);
  const [now, setNow] = useState(() => Date.now());

  // Tick every second while a countdown is on screen (resend timer, lockout, cooldown).
  const counting = step.kind === 'code' || problem?.code === 'locked' || problem?.code === 'cooldown';
  useEffect(() => {
    if (!counting) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [counting]);

  const locked = problem?.code === 'locked' && now < problem.until;
  const resendWait = step.kind === 'code' ? Math.max(0, Math.ceil((step.sentAt + step.resendAfter * 1000 - now) / 1000)) : 0;

  const handle = (err: ApiError) => {
    setNow(Date.now());
    setProblem(problemOf(err));
  };

  const requestCode = async (channel: LoginChannel) => {
    setProblem(null);
    setBusy(true);
    try {
      const number = step.kind === 'code' ? step.phone : phone;
      const r = await api<CodeResponse>('/auth/code', { method: 'POST', body: JSON.stringify({ phone: number, lang, channel }) });
      setStep({
        kind: 'code',
        phone: r.phone,
        channel: r.channel,
        display: r.channel === 'email' ? (r.email_display ?? '') : r.phone_display,
        sentAt: Date.now(),
        resendAfter: r.resend_after,
      });
      setCode('');
    } catch (err) {
      handle(err as ApiError);
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    if (step.kind !== 'code') return;
    setProblem(null);
    setBusy(true);
    try {
      const me = await api<Me>('/auth/session', { method: 'POST', body: JSON.stringify({ phone: step.phone, code, lang }) });
      onSignedIn(me);
    } catch (err) {
      handle(err as ApiError);
    } finally {
      setBusy(false);
    }
  };

  const message = problem ? problemText(problem, lang, now) : null;

  return (
    <AuthLayout lang={lang} setLang={setLang}>
      <p className="eyebrow auth-eyebrow">{t.design_restaurant_space}</p>
      <header className="signin-heading">
        <h1 className="display">{t.design_welcome}</h1>
        <p>{method === 'email' ? t.signin_email_intro : t.signin_intro}</p>
      </header>
      <div className="signin-tabs" role="tablist" aria-label={t.signin_methods}>
        {(['email', 'phone'] as const).map((option) => (
          <button
            key={option}
            id={`signin-tab-${option}`}
            type="button"
            role="tab"
            aria-selected={method === option}
            aria-controls={`signin-panel-${option}`}
            tabIndex={method === option ? 0 : -1}
            disabled={busy}
            onClick={() => setMethod(option)}
            onKeyDown={(e) => {
              if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) return;
              e.preventDefault();
              const next = e.key === 'Home' ? 'email' : e.key === 'End' ? 'phone' : option === 'email' ? 'phone' : 'email';
              setMethod(next);
              document.getElementById(`signin-tab-${next}`)?.focus();
            }}
          >
            <Icon name={option === 'email' ? 'mail' : 'phone'} />
            {option === 'email' ? t.signin_email : t.signin_phone}
          </button>
        ))}
      </div>
      <div id="signin-panel-email" role="tabpanel" aria-labelledby="signin-tab-email" hidden={method !== 'email'}>
        <CredentialsLogin lang={lang} onSignedIn={onSignedIn} busy={busy} setBusy={setBusy} />
      </div>
      <div id="signin-panel-phone" role="tabpanel" aria-labelledby="signin-tab-phone" hidden={method !== 'phone'}>
        {step.kind === 'phone' ? (
          <PhoneForm t={t} phone={phone} setPhone={setPhone} busy={busy} locked={locked} message={message} request={(ch) => void requestCode(ch)} />
        ) : (
          <CodeForm
            t={t}
            step={step}
            code={code}
            setCode={setCode}
            busy={busy}
            locked={locked}
            message={message}
            resendWait={resendWait}
            verify={() => void verify()}
            request={(ch) => void requestCode(ch)}
            changePhone={() => {
              setStep({ kind: 'phone' });
              setProblem(null);
              setCode('');
            }}
          />
        )}
      </div>
    </AuthLayout>
  );
}
